import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { NgIf } from '@angular/common';

/**
 * The spinner a filing sheet shows while its schema loads. **One loader for
 * every sheet.**
 *
 * Usage — the loader is a SIBLING of the card, never a child of it:
 * ```html
 * <app-sheet-loader [visible]="isLoading" />
 *
 * <p-card *ngIf="!isLoading && schema && form" class="form-card">
 *   …
 * </p-card>
 * ```
 *
 * ## Why it exists — the shape it replaces could not render
 *
 * Before this component, every sheet hand-rolled the same eight lines of
 * markup, and they had drifted into four different shapes. Thirteen of the
 * twenty-six sheets had written it as:
 *
 * ```html
 * <p-card *ngIf="!isLoading && schema && form">   <!-- gate -->
 *   <div *ngIf="isLoading" class="loading-container">…</div>   <!-- unreachable -->
 * ```
 *
 * The loader sat INSIDE the element whose own condition is its negation, so
 * `isLoading` being true removed the card and took the spinner with it. Those
 * thirteen sheets rendered **nothing at all** while loading — and because the
 * only on-screen evidence of a stalled load is this spinner, a sheet whose
 * `isLoading` never cleared presented as a permanently blank page with no
 * diagnostic signal. Roles 13 (Analysis of Profit or Loss) and 23 (Notes –
 * Issued Capital), the two sheets reported stuck, were both in that set.
 *
 * Hoisting the loader out of the gate is therefore not cosmetic: it is what
 * makes a stall observable. `scripts/check-sheet-loaders.mjs`
 * (`npm run check:sheet-loaders`)
 * reads all 26 sheet templates and fails if a loader is ever nested inside an
 * `!isLoading`-gated element again — a structural guard, because no behavioural
 * spec can fail on markup that simply never renders.
 *
 * The stall itself (a hydration await that never settles) is described under
 * `STALL` in `docs/development/filing-load-diagnostics.md`.
 *
 * ## Styling
 *
 * Self-sufficient. The flex block below was previously copy-pasted
 * byte-identical into ~24 per-sheet stylesheets as `.loading-container`, and
 * the icon's `font-size` lived in an inline `style` attribute on every sheet.
 * Both now live here once. The per-sheet `.loading-container` rules were
 * deleted with the markup they styled — view encapsulation means a sheet-level
 * rule could not reach into this component anyway, so keeping them would have
 * left dead CSS that looks load-bearing.
 *
 * ## Copy
 *
 * The label is a plain "Loading..." (PO, 2026-08-13). It used to read "Loading
 * form schema...", which named an implementation detail the preparer has no
 * stake in; role 12 had additionally drifted to "Loading from schema...".
 *
 * ## Not only the sheets — the filing container's hydration loader too
 *
 * `filing-form-container.component.html`'s `#hydratingLoader` renders this as
 * well (2026-08-21, PO: one loader look). Entering a conversion is TWO
 * consecutive waits — the container hydrating its IndexedDB state, then the
 * sheet fetching its schema — and the first used to be a four-bar
 * `<p-skeleton>` stack captioned "Loading filing data…". A preparer opening a
 * conversion could therefore watch a skeleton become a spinner before anything
 * rendered, for what is one load to them.
 *
 * That the two rarely appeared together is why it survived: hydration normally
 * resolves too fast for the skeleton to register. The last-tab-out liveness
 * probe changed that — a fresh load carrying unsaved edits with no sibling to
 * answer waits out `CrossTabSyncService.LIVENESS_PROBE_TIMEOUT_MS` — which is
 * how the divergence finally became visible rather than merely present.
 *
 * **A new wait on the filing entry path belongs here too.** `<p-skeleton>` is
 * still right for placeholders that stand in for a specific piece of content
 * (the sheet-selector status circles), but not for "the page is loading".
 */
@Component({
  selector: 'app-sheet-loader',
  standalone: true,
  imports: [NgIf],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div *ngIf="visible" class="loading-container">
      <i class="pi pi-spin pi-spinner"></i>
      <p>{{ label }}</p>
    </div>
  `,
  styles: [
    `
      .loading-container {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 3rem;
        gap: 1rem;
      }

      .loading-container i {
        font-size: 2rem;
      }

      .loading-container p {
        margin: 0;
      }
    `,
  ],
})
export class SheetLoaderComponent {
  /**
   * Render the spinner when true. Callers pass their own `isLoading` flag —
   * `isLoading()` on the sheets that hold it as a signal (roles 9 and 19).
   */
  @Input() visible: boolean | null | undefined = false;

  /**
   * Overrides the "Loading..." copy. The schema-load spinner on all 26 sheets
   * takes the default, and so does the filing container's hydration loader (see
   * the class docblock); role 19's dev-only debug-layout swap is the one caller
   * that sets it ("Switching layout…"), so that spinner shares this
   * implementation rather than re-hand-rolling `.loading-container` — which is
   * what it did before, and which left it silently unstyled the moment the
   * per-sheet CSS rule was consolidated in here.
   */
  @Input() label = 'Loading...';
}
